import { useState } from 'react';
import {
  ShieldAlert, CheckCircle2, Circle, XCircle, AlertTriangle, Upload, Clock,
  FileWarning, Scale, Globe, Trash2, Calculator, Mail, ChevronRight, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api/client';

// ==================== Helpers ====================

const fmtAUD = (v: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 }).format(v);

// ==================== Mock Data ====================

interface ComplianceItem {
  label: string;
  checked: boolean;
  evidence?: string;
}

interface ComplianceGroup {
  category: string;
  items: ComplianceItem[];
}

const INITIAL_CHECKLIST: ComplianceGroup[] = [
  {
    category: 'Trust Deed',
    items: [
      { label: 'Trust deed is current and up to date', checked: true },
      { label: 'Trust deed reviewed within last 3 years', checked: true },
    ],
  },
  {
    category: 'Investment Strategy',
    items: [
      { label: 'Investment strategy reviewed and signed', checked: true },
      { label: 'Insurance needs considered (s.52(7)(c) SIS)', checked: false },
      { label: 'Diversification adequately addressed', checked: true },
    ],
  },
  {
    category: 'Minutes',
    items: [
      { label: 'Annual meeting of members/trustees held', checked: true },
      { label: 'Trustee declarations signed', checked: true },
      { label: 'Minutes of all investment decisions recorded', checked: false },
    ],
  },
  {
    category: 'Contributions',
    items: [
      { label: 'All contributions within caps', checked: true },
      { label: 's290-170 notices lodged (deduction notices)', checked: true },
      { label: 'Contribution reserving strategy documented', checked: false },
    ],
  },
  {
    category: 'Pensions',
    items: [
      { label: 'Minimum pension amounts met', checked: true },
      { label: 'TBAR lodged with ATO', checked: true },
      { label: 'Pension documentation current', checked: true },
    ],
  },
  {
    category: 'Assets',
    items: [
      { label: 'All asset valuations current (market value)', checked: true },
      { label: 'In-house assets below 5% threshold', checked: true },
      { label: 'All assets held in fund name or bare trust', checked: true },
    ],
  },
  {
    category: 'Lodgement',
    items: [
      { label: 'SAR lodged on time', checked: false },
      { label: 'Income tax paid / refund received', checked: false },
    ],
  },
];

interface SolePurposeReview {
  fundName: string;
  score: number;
  risk: 'low' | 'medium' | 'high';
  flagged: { item: string; explanation: string }[];
  recommendations: string[];
}

const SOLE_PURPOSE_REVIEWS: SolePurposeReview[] = [
  {
    fundName: 'Smith Family Super Fund',
    score: 92,
    risk: 'low',
    flagged: [
      { item: 'Holiday property held as investment', explanation: 'Property at 42 Ocean Drive, Byron Bay, confirmed leased at arm\'s length. No personal use detected.' },
    ],
    recommendations: [
      'Maintain arm\'s length lease documentation for all property assets',
      'Consider obtaining independent rental appraisal annually',
    ],
  },
  {
    fundName: 'Johnson Retirement Fund',
    score: 68,
    risk: 'medium',
    flagged: [
      { item: 'Loan to related party entity', explanation: 'Loan of $85,000 to Johnson Holdings Pty Ltd identified. Potential s.65 contravention if not on commercial terms.' },
      { item: 'Artwork held at trustee residence', explanation: 'Collectible artwork valued at $45,000 stored at trustee\'s home address. Must be stored independently per s.62A SIS Reg.' },
      { item: 'Related party property lease below market', explanation: 'Commercial premises leased to related entity at 15% below market rate. Requires arm\'s length terms.' },
    ],
    recommendations: [
      'Immediately review and rectify related party loan terms or repay loan',
      'Arrange independent storage for collectible artwork assets',
      'Obtain independent market rental valuation for commercial property',
      'Consider engaging SMSF specialist for compliance remediation',
    ],
  },
];

interface Trustee {
  name: string;
  role: string;
  checks: { label: string; clear: boolean }[];
}

const MOCK_TRUSTEES: Trustee[] = [
  {
    name: 'John Smith',
    role: 'Individual Trustee',
    checks: [
      { label: 'Undischarged bankrupt', clear: true },
      { label: 'Disqualified by ATO/APRA', clear: true },
      { label: 'Convicted of dishonesty offence', clear: true },
      { label: 'Under 18 years of age', clear: true },
    ],
  },
  {
    name: 'Jane Smith',
    role: 'Individual Trustee',
    checks: [
      { label: 'Undischarged bankrupt', clear: true },
      { label: 'Disqualified by ATO/APRA', clear: true },
      { label: 'Convicted of dishonesty offence', clear: true },
      { label: 'Under 18 years of age', clear: true },
    ],
  },
];

interface ResidencyTest {
  fundName: string;
  centralManagement: boolean;
  activeMember: boolean;
  assetTest: boolean;
}

const MOCK_RESIDENCY: ResidencyTest[] = [
  { fundName: 'Smith Family Super Fund', centralManagement: true, activeMember: true, assetTest: true },
  { fundName: 'Johnson Retirement Fund', centralManagement: true, activeMember: true, assetTest: true },
];

interface WindUpStep {
  step: number;
  label: string;
  description: string;
  status: 'completed' | 'current' | 'pending';
}

const WIND_UP_STEPS: WindUpStep[] = [
  { step: 1, label: 'Board Resolution', description: 'Trustees resolve to wind up the fund and document in minutes', status: 'pending' },
  { step: 2, label: 'Notify ATO', description: 'Lodge notification with ATO of intention to wind up', status: 'pending' },
  { step: 3, label: 'Pay Liabilities', description: 'Settle all outstanding debts, tax, and expenses', status: 'pending' },
  { step: 4, label: 'Dispose Assets', description: 'Sell or transfer all fund assets at market value', status: 'pending' },
  { step: 5, label: 'Pay Benefits', description: 'Roll over or pay member benefits to complying funds or as lump sums', status: 'pending' },
  { step: 6, label: 'Final SAR', description: 'Prepare and lodge final SMSF Annual Return', status: 'pending' },
  { step: 7, label: 'Cancel ABN/TFN', description: 'Cancel the fund\'s ABN and TFN with the ATO', status: 'pending' },
];

interface Penalty {
  contravention: string;
  sisSection: string;
  penaltyAmount: string;
  educationDirection: boolean;
}

const MOCK_PENALTIES: Penalty[] = [
  { contravention: 'Loan to member or relative', sisSection: 's.65', penaltyAmount: '$18,780 per contravention', educationDirection: true },
  { contravention: 'Sole purpose test breach', sisSection: 's.62(1) / s.84', penaltyAmount: '$18,780 per contravention', educationDirection: true },
  { contravention: 'In-house assets exceed 5%', sisSection: 's.71', penaltyAmount: '$18,780 per contravention', educationDirection: true },
  { contravention: 'Arm\'s length rule breach', sisSection: 's.109', penaltyAmount: '$18,780 per contravention', educationDirection: true },
  { contravention: 'Borrowing contravention', sisSection: 's.67', penaltyAmount: '$18,780 per contravention', educationDirection: true },
  { contravention: 'Late lodgement of SAR', sisSection: 'TAA s.286-75', penaltyAmount: '$313 per 28-day period (max 5 periods)', educationDirection: false },
  { contravention: 'Failure to prepare financial statements', sisSection: 's.35B', penaltyAmount: '$3,130', educationDirection: false },
];

const PENALTY_CALCULATOR_OPTIONS = MOCK_PENALTIES.map(p => ({
  value: p.sisSection,
  label: `${p.contravention} (${p.sisSection})`,
}));

interface Correspondence {
  dateReceived: string;
  subject: string;
  type: 'query' | 'audit' | 'assessment' | 'direction';
  responseDue: string;
  status: 'pending' | 'responded' | 'resolved';
}

const MOCK_CORRESPONDENCE: Correspondence[] = [
  { dateReceived: '12/11/2026', subject: 'Request for information - FY2025/26 SAR', type: 'query', responseDue: '12/12/2026', status: 'pending' },
  { dateReceived: '03/09/2026', subject: 'SMSF compliance audit notification', type: 'audit', responseDue: '03/10/2026', status: 'responded' },
  { dateReceived: '15/06/2026', subject: 'Amendment to income tax assessment', type: 'assessment', responseDue: '15/07/2026', status: 'resolved' },
  { dateReceived: '22/03/2026', subject: 'Education direction - trustee duties', type: 'direction', responseDue: '22/04/2026', status: 'resolved' },
];

const TYPE_BADGE: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  query: { label: 'Query', variant: 'outline' },
  audit: { label: 'Audit', variant: 'destructive' },
  assessment: { label: 'Assessment', variant: 'secondary' },
  direction: { label: 'Direction', variant: 'default' },
};

const CORR_STATUS_BADGE: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  pending: { label: 'Pending', variant: 'outline' },
  responded: { label: 'Responded', variant: 'secondary' },
  resolved: { label: 'Resolved', variant: 'default' },
};

// ==================== Component ====================

export default function SMSFComplianceRiskPage() {
  const [tab, setTab] = useState('checklist');
  const [selectedFundId, setSelectedFundId] = useState<string>('');
  const [checklist, setChecklist] = useState(INITIAL_CHECKLIST);
  const [selectedPenalty, setSelectedPenalty] = useState('');

  // Fetch funds list
  const { data: fundsData, isLoading: fundsLoading } = useQuery({
    queryKey: ['smsf-funds'],
    queryFn: async () => { const r = await apiClient.get('/smsf/funds'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  const fundId = selectedFundId || fundsData?.[0]?.id || '';

  // Fetch compliance for selected fund
  const { data: complianceData, isLoading: complianceLoading } = useQuery({
    queryKey: ['smsf-compliance', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/compliance`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!fundId,
  });

  // Fetch trustees for selected fund
  const { data: trusteesData, isLoading: trusteesLoading } = useQuery({
    queryKey: ['smsf-trustees', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/trustees`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!fundId,
  });

  // Fetch lodgements for selected fund
  const { data: lodgementsData, isLoading: lodgementsLoading } = useQuery({
    queryKey: ['smsf-lodgements', fundId],
    queryFn: async () => { const r = await apiClient.get(`/smsf/funds/${fundId}/lodgements`); return r.data.data; },
    staleTime: 5 * 60 * 1000,
    enabled: !!fundId,
  });

  // Use API data with mock fallback
  const solePurposeReviews = complianceData?.solePurposeReviews || SOLE_PURPOSE_REVIEWS;
  const trustees: Trustee[] = trusteesData || MOCK_TRUSTEES;
  const residency = complianceData?.residency || MOCK_RESIDENCY;
  const correspondence = lodgementsData?.correspondence || MOCK_CORRESPONDENCE;

  const isLoading = fundsLoading || complianceLoading || trusteesLoading || lodgementsLoading;

  const totalItems = checklist.reduce((s, g) => s + g.items.length, 0);
  const checkedItems = checklist.reduce((s, g) => s + g.items.filter(i => i.checked).length, 0);
  const checklistPct = Math.round((checkedItems / totalItems) * 100);

  const toggleItem = (groupIdx: number, itemIdx: number) => {
    setChecklist(prev => {
      const next = prev.map((g, gi) => gi === groupIdx ? {
        ...g,
        items: g.items.map((item, ii) => ii === itemIdx ? { ...item, checked: !item.checked } : item),
      } : g);
      return next;
    });
  };

  const matchedPenalty = MOCK_PENALTIES.find(p => p.sisSection === selectedPenalty);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={ShieldAlert}
        title="SMSF Compliance & Risk"
        description="Compliance monitoring, risk assessment, and regulatory management"
      />

      {/* Fund Selector */}
      {fundsData && fundsData.length > 0 && (
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-muted-foreground">Fund:</label>
          <Select value={fundId} onValueChange={setSelectedFundId}>
            <SelectTrigger className="w-[320px]"><SelectValue placeholder="Select a fund..." /></SelectTrigger>
            <SelectContent>
              {fundsData.map((f: any) => (
                <SelectItem key={f.id} value={f.id}>{f.fund_name || f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="checklist">Compliance Checklist</TabsTrigger>
          <TabsTrigger value="sole-purpose">Sole Purpose AI</TabsTrigger>
          <TabsTrigger value="disqualified">Disqualified Persons</TabsTrigger>
          <TabsTrigger value="residency">Residency</TabsTrigger>
          <TabsTrigger value="windup">Wind-Up</TabsTrigger>
          <TabsTrigger value="penalties">Penalties</TabsTrigger>
          <TabsTrigger value="correspondence">ATO Correspondence</TabsTrigger>
        </TabsList>

        {/* ===== Compliance Checklist ===== */}
        <TabsContent value="checklist" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Annual Compliance Checklist</CardTitle>
                <Badge variant={checklistPct === 100 ? 'default' : 'secondary'}>{checklistPct}% Complete ({checkedItems}/{totalItems})</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <Progress value={checklistPct} className="h-2.5 mb-6" />
              <div className="space-y-6">
                {checklist.map((group, gi) => (
                  <div key={group.category}>
                    <p className="text-sm font-semibold mb-2">{group.category}</p>
                    <div className="space-y-2">
                      {group.items.map((item, ii) => (
                        <div key={ii} className="flex items-center gap-3">
                          <Checkbox
                            checked={item.checked}
                            onCheckedChange={() => toggleItem(gi, ii)}
                          />
                          <span className={cn('text-sm', item.checked ? '' : 'text-muted-foreground')}>{item.label}</span>
                          <Button variant="ghost" size="sm" className="ml-auto h-7 text-xs gap-1" onClick={() => toast.info('Evidence upload coming soon')}>
                            <Upload className="h-3 w-3" /> Evidence
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Sole Purpose AI ===== */}
        <TabsContent value="sole-purpose" className="space-y-4">
          {solePurposeReviews.map((review: any) => {
            const riskColor = review.risk === 'low' ? 'text-green-600' : review.risk === 'medium' ? 'text-amber-600' : 'text-red-600';
            const riskBg = review.risk === 'low' ? 'bg-green-500' : review.risk === 'medium' ? 'bg-amber-500' : 'bg-red-500';
            const scoreBg = review.score >= 80 ? 'bg-green-500/10 text-green-700' : review.score >= 60 ? 'bg-amber-500/10 text-amber-700' : 'bg-red-500/10 text-red-700';
            return (
              <Card key={review.fundName}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{review.fundName}</CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge className={cn(scoreBg, 'border-0 font-bold text-base px-3 py-1')}>{review.score}/100</Badge>
                      <Badge variant="outline" className={cn(riskColor)}>
                        <span className={cn('h-2 w-2 rounded-full mr-1.5', riskBg)} />
                        {review.risk.charAt(0).toUpperCase() + review.risk.slice(1)} Risk
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {review.flagged.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2">Flagged Items</p>
                      <div className="space-y-2">
                        {review.flagged.map((f, i) => (
                          <div key={i} className="border rounded-lg p-3">
                            <div className="flex items-start gap-2">
                              <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                              <div>
                                <p className="text-sm font-medium">{f.item}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{f.explanation}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">Recommendations</p>
                    <ul className="space-y-1.5">
                      {review.recommendations.map((r, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* ===== Disqualified Persons ===== */}
        <TabsContent value="disqualified" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Trustee Disqualification Checks</CardTitle>
              <CardDescription>Verification under s.120 SIS Act - persons disqualified from being a trustee</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Trustee</th>
                      <th className="pb-2 pr-4 font-medium">Role</th>
                      <th className="pb-2 pr-4 font-medium">Undischarged Bankrupt</th>
                      <th className="pb-2 pr-4 font-medium">Disqualified by Regulator</th>
                      <th className="pb-2 pr-4 font-medium">Dishonesty Conviction</th>
                      <th className="pb-2 pr-4 font-medium">Under 18</th>
                      <th className="pb-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trustees.map((t) => {
                      const allClear = t.checks.every(c => c.clear);
                      return (
                        <tr key={t.name} className="border-b last:border-0">
                          <td className="py-3 pr-4 font-medium">{t.name}</td>
                          <td className="py-3 pr-4 text-muted-foreground">{t.role}</td>
                          {t.checks.map((c, i) => (
                            <td key={i} className="py-3 pr-4 text-center">
                              {c.clear
                                ? <CheckCircle2 className="h-4 w-4 text-green-600 inline" />
                                : <XCircle className="h-4 w-4 text-red-600 inline" />}
                            </td>
                          ))}
                          <td className="py-3">
                            <Badge variant={allClear ? 'default' : 'destructive'}>
                              {allClear ? 'Clear' : 'Flagged'}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Residency ===== */}
        <TabsContent value="residency" className="space-y-4">
          {residency.map((fund: any) => {
            const allPass = fund.centralManagement && fund.activeMember && fund.assetTest;
            return (
              <Card key={fund.fundName}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Globe className="h-4 w-4" /> {fund.fundName}
                    </CardTitle>
                    <Badge variant={allPass ? 'default' : 'destructive'} className="gap-1">
                      {allPass ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                      {allPass ? 'Australian Resident Fund' : 'Non-Resident Warning'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {[
                      { label: 'Central Management & Control', pass: fund.centralManagement, desc: 'Strategic decisions made in Australia' },
                      { label: 'Active Member Test', pass: fund.activeMember, desc: 'Active members are Australian residents' },
                      { label: 'Asset Test', pass: fund.assetTest, desc: 'Fund assets situated in Australia' },
                    ].map((test) => (
                      <div key={test.label} className="border rounded-lg p-3 text-center">
                        {test.pass
                          ? <CheckCircle2 className="h-6 w-6 text-green-600 mx-auto mb-1" />
                          : <XCircle className="h-6 w-6 text-red-600 mx-auto mb-1" />}
                        <p className="text-sm font-medium">{test.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{test.desc}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* ===== Wind-Up ===== */}
        <TabsContent value="windup" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Trash2 className="h-4 w-4" /> Fund Wind-Up Wizard
                  </CardTitle>
                  <CardDescription>Step-by-step process for winding up an SMSF</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => toast.info('Wind-up process initiation coming soon')}
                >
                  Start Wind-Up
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {WIND_UP_STEPS.map((step) => {
                  const Icon = step.status === 'completed' ? CheckCircle2
                    : step.status === 'current' ? Clock : Circle;
                  const color = step.status === 'completed' ? 'text-green-600 border-green-200 bg-green-50'
                    : step.status === 'current' ? 'text-amber-600 border-amber-200 bg-amber-50'
                    : 'text-muted-foreground border-border';
                  return (
                    <div key={step.step} className={cn('border rounded-lg p-4 flex items-start gap-3', color)}>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs font-bold w-5 text-center">{step.step}</span>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{step.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Penalties ===== */}
        <TabsContent value="penalties" className="space-y-4">
          {/* Penalty Calculator */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Calculator className="h-4 w-4" /> Penalty Calculator
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-3 items-start">
                <Select value={selectedPenalty} onValueChange={setSelectedPenalty}>
                  <SelectTrigger className="w-full sm:w-[420px]">
                    <SelectValue placeholder="Select a contravention..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PENALTY_CALCULATOR_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {matchedPenalty && (
                  <div className="border rounded-lg p-3 flex-1 w-full">
                    <p className="text-sm"><span className="font-medium">Penalty:</span> {matchedPenalty.penaltyAmount}</p>
                    <p className="text-sm mt-1">
                      <span className="font-medium">Education Direction:</span>{' '}
                      {matchedPenalty.educationDirection ? 'Yes - Trustee education may be required' : 'No'}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Penalties Table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileWarning className="h-4 w-4" /> Common SMSF Contraventions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Contravention</th>
                      <th className="pb-2 pr-4 font-medium">SIS Section</th>
                      <th className="pb-2 pr-4 font-medium">Penalty</th>
                      <th className="pb-2 font-medium">Education Direction</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MOCK_PENALTIES.map((p) => (
                      <tr key={p.sisSection} className="border-b last:border-0">
                        <td className="py-3 pr-4 font-medium">{p.contravention}</td>
                        <td className="py-3 pr-4 font-mono text-xs">{p.sisSection}</td>
                        <td className="py-3 pr-4">{p.penaltyAmount}</td>
                        <td className="py-3">
                          <Badge variant={p.educationDirection ? 'secondary' : 'outline'}>
                            {p.educationDirection ? 'Required' : 'N/A'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== ATO Correspondence ===== */}
        <TabsContent value="correspondence" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Mail className="h-4 w-4" /> ATO Correspondence
                  </CardTitle>
                  <CardDescription>Track all communications with the Australian Taxation Office</CardDescription>
                </div>
                <Button variant="outline" size="sm" className="gap-1" onClick={() => toast.info('Upload coming soon')}>
                  <Upload className="h-3.5 w-3.5" /> Upload
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Date Received</th>
                      <th className="pb-2 pr-4 font-medium">Subject</th>
                      <th className="pb-2 pr-4 font-medium">Type</th>
                      <th className="pb-2 pr-4 font-medium">Response Due</th>
                      <th className="pb-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {correspondence.map((c: any, i: number) => {
                      const tp = TYPE_BADGE[c.type];
                      const st = CORR_STATUS_BADGE[c.status];
                      return (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-3 pr-4 text-muted-foreground whitespace-nowrap">{c.dateReceived}</td>
                          <td className="py-3 pr-4 font-medium">{c.subject}</td>
                          <td className="py-3 pr-4"><Badge variant={tp.variant}>{tp.label}</Badge></td>
                          <td className="py-3 pr-4 text-muted-foreground whitespace-nowrap">{c.responseDue}</td>
                          <td className="py-3"><Badge variant={st.variant}>{st.label}</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
